import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, X, Info } from 'lucide-react';
import { useCampaignAPI } from '@/hooks/campaigns/useCampaignAPI';
import useCampaignPersistence from '@/hooks/campaigns/useCampaignPersistence';
import StepIndicator from './StepIndicator';
import CampaignDetailsForm from '@/components/launch-campaign/CampaignDetailsForm';
import POCSelectionForm from './POCSelectionForm';
import CreativeUploadForm from './CreativeUploadForm';
import CampaignSubmissionSuccess from '@/components/common-components/dialogBoxes/CampaignSubmissionSuccess';

const STEPS = [
  { label: 'Campaign Details', subtitle: 'DETAILS' },
  { label: 'POC Selection', subtitle: 'SELECTION' },
  { label: 'Creative Upload', subtitle: 'UPLOAD' }
];

const NewCampaignCreation = ({ selectedRegion, selectedBrand }) => {
  const { 
    createNewCampaign,
    updateExistingCampaign,
    getCampaignOptions, 
    uploadCreatives,
    updateCreativesForCampaign,
    addPocsToCampaign,
    loading,
  } = useCampaignAPI();

  // Use campaign persistence hook
  const {
    expandedStep,
    completedSteps,
    campaignData,
    selectedPOCs,
    creativeData,
    hasPersistedData,
    hasDataToConfirm,
    setExpandedStep,
    setCampaignData,
    setSelectedPOCs,
    setCreativeData,
    completeStep,
    resetCampaign,
    ensureStep1Completed
  } = useCampaignPersistence(selectedRegion, selectedBrand);

  const [currentStep, setCurrentStep] = useState(1);
  const [showCacheNotification, setShowCacheNotification] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  
  // Refs to access form validation methods
  const step1FormRef = React.useRef(null);
  const step2FormRef = React.useRef(null);
  const step3FormRef = React.useRef(null);

  // console.log("Campaign Data in NewCampaignCreation: ", campaignData);
  // console.log("Persisted Data Status:", { hasPersistedData, completedSteps, expandedStep });

  // Ensure step 1 is marked complete if campaign was previously created
  useEffect(() => {
    ensureStep1Completed();
  }, [ensureStep1Completed]);

  // Check if there's cached data to show notification
  useEffect(() => {
    if (hasPersistedData) {
      setShowCacheNotification(true);
      const timer = setTimeout(() => {
        setShowCacheNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [hasPersistedData]);

  // Sync current step with persisted data on mount
  useEffect(() => {
    if (hasPersistedData && campaignData?.campaignId) {
      // If we have a campaign ID, we can at least be on step 2
      if (completedSteps.includes(2) || selectedPOCs.length > 0) {
        setCurrentStep(3);
        setExpandedStep(3);
      } else if (completedSteps.includes(1) || campaignData.campaignId) {
        setCurrentStep(2);
        setExpandedStep(2);
      }
    }
  }, []);

  // Fetch options on component mount
  useEffect(() => {
    getCampaignOptions();
  }, [getCampaignOptions]);

  // Sync current step with expanded step (but don't override persisted state on mount)
  useEffect(() => {
    if (expandedStep && !hasPersistedData) {
      setCurrentStep(expandedStep);
    }
  }, [expandedStep]);


  const handleSaveDraft = () => {
    alert('Campaign draft saved successfully!');
  };

  // Step 1: Create or Update Campaign
  const handleStep1Next = async () => {
    // Validate form first
    if (step1FormRef.current && !step1FormRef.current.validate()) {
      return;
    }

    try {
      const fullCampaignData = {
        ...campaignData,
        region: selectedRegion,
        brand: selectedBrand
      };

      // Check if we're editing an existing campaign or creating a new one
      const isEditMode = !!campaignData.campaignId;
      
      // console.log(`${isEditMode ? 'Updating' : 'Creating'} campaign with data:`, fullCampaignData);
      
      let result;
      if (isEditMode) {
        // PUT request to update existing campaign
        result = await updateExistingCampaign(campaignData.campaignId, fullCampaignData);
        // console.log('Campaign update result:', result);
      } else {
        // POST request to create new campaign
        result = await createNewCampaign(fullCampaignData);
        // console.log('Campaign creation result:', result);
      }
      
      if (result) {
        setCampaignData(prev => ({ 
          ...prev, 
          campaignId: result.campaign_id || campaignData.campaignId
        }));
        completeStep(1, { ...campaignData, campaignId: result.campaign_id || campaignData.campaignId });
        setCurrentStep(2);
        setExpandedStep(2);
        alert(`Campaign "${result.campaign_name}" ${isEditMode ? 'updated' : 'created'} successfully with ID: ${result.campaign_id || campaignData.campaignId}`);
      }
    } catch (error) {
      console.error('Error processing campaign:', error);
      alert(`Failed to process campaign: ${error.message}`);
    }
  };

  // Step 2: Add POCs
  const handleStep2Next = async () => {
    // Validate form first
    if (step2FormRef.current && !step2FormRef.current.validate()) {
      return;
    }

    try {
      const campaignId = campaignData.campaignId;
      if (!campaignId) {
        throw new Error('Campaign ID not found');
      }

      // Get POC data including allocation mode and budget info
      const pocFormData = step2FormRef.current.getData ? step2FormRef.current.getData() : { selectedPocs: selectedPOCs };
      
      // console.log('Adding POCs to campaign:', campaignId, pocFormData);
      const result = await addPocsToCampaign(campaignId, pocFormData.selectedPocs || selectedPOCs, {
        budget_allocation_mode: pocFormData.budgetAllocationModeEnum,
        budget_per_poc: pocFormData.budgetPerPoc
      });
      // console.log('POCs addition result:', result);
      
      if (result) {
        completeStep(2, selectedPOCs);
        setCurrentStep(3);
        setExpandedStep(3);
        alert(`${selectedPOCs.length} POCs added successfully to campaign "${campaignData.campaignName}"`);
      }
    } catch (error) {
      console.error('Error adding POCs:', error);
      alert(`Failed to add POCs: ${error.message}`);
    }
  };

  const handleStep2Back = () => {
    setCurrentStep(1);
    setExpandedStep(1);
  };

  // Step 3: Upload or Update Creatives
  const handleStep3Submit = async () => {
    // Validate and get form data
    if (step3FormRef.current && !step3FormRef.current.validate()) {
      return;
    }

    const creativeFormData = step3FormRef.current.getData();

    try {
      const campaignId = campaignData.campaignId;
      if (!campaignId) {
        throw new Error('Campaign ID not found');
      }

      // Check if we're editing an existing campaign
      // We can determine this by checking if the campaign had pre-existing creatives
      // For simplicity, we'll check if campaignData had a campaignId when we started step 1
      const isEditMode = !!campaignData.isEditMode; // This should be set when loading existing campaign
      
      // console.log(`${isEditMode ? 'Updating' : 'Uploading'} creatives to campaign:`, campaignId, creativeFormData);
      
      let result;
      if (isEditMode) {
        // PUT request to update existing creatives
        result = await updateCreativesForCampaign(campaignId, creativeFormData);
        // console.log('Creative update result:', result);
      } else {
        // POST request to upload new creatives
        result = await uploadCreatives(campaignId, creativeFormData);
        // console.log('Creative upload result:', result);
      }
      
      if (result) {
        completeStep(3, creativeFormData);
        setCreativeData(creativeFormData);
        
        // Show success screen instead of alert
        setShowSuccessScreen(true);
      }
    } catch (error) {
      console.error('Error processing creatives:', error);
      alert(`Failed to process creatives: ${error.message}`);
    }
  };

  const handleStep3Back = () => {
    setCurrentStep(2);
    setExpandedStep(2);
  };

  const handleViewCampaigns = () => {
    resetCampaign();
    window.location.href = '/campaigns';
  };

  const handleCreateNewCampaign = () => {
    resetCampaign();
    setShowSuccessScreen(false);
    setCurrentStep(1);
    setExpandedStep(1);
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all campaign data? This action cannot be undone.')) {
      resetCampaign();
      setCurrentStep(1);
      setExpandedStep(1);
      setShowCacheNotification(false);
      alert('Campaign data cleared successfully');
    }
  };

  // Show success screen if campaign was submitted
  if (showSuccessScreen) {
    let submitterEmail =
      campaignData.bdrEmail || campaignData.bdr_email || '';
    if (!submitterEmail) {
      try {
        const authUser = sessionStorage.getItem('auth_user');
        if (authUser) {
          submitterEmail = JSON.parse(authUser)?.email || '';
        }
      } catch {
        // ignore
      }
    }

    return (
      <CampaignSubmissionSuccess
        campaignName={campaignData.campaignName}
        campaignId={campaignData.campaignId}
        brand={selectedBrand || campaignData.brand}
        region={selectedRegion || campaignData.region}
        submitterEmail={submitterEmail}
        campaignDetails={campaignData}
        selectedPOCs={selectedPOCs}
        creativeDetails={creativeData}
        onViewCampaigns={handleViewCampaigns}
        onCreateNew={handleCreateNewCampaign}
      />
    );
  }

  return (
    <div className="bg-gray-50 rounded-3xl">
      {/* Header with Brand, Region and Clear Data Button */}
      <div className="max-w-7xl mx-auto px-8 pt-8">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">New Ad Request</h1>
            <div className="flex items-center gap-2 text-sm">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 font-semibold rounded-lg">
                Region: {selectedRegion}
              </span>
              <span className="text-gray-400">|</span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 font-semibold rounded-lg">
                Brand: {selectedBrand}
              </span>
            </div>
          </div>
          
          {/* Clear Data Button */}
          {hasDataToConfirm && (
            <Button
              variant="outline"
              onClick={handleClearData}
              className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
            >
              <Trash2 className="w-4 h-4" />
              Clear Data
            </Button>
          )}
        </div>
      </div>
    
      {/* Cache Notification */}
      {showCacheNotification && (
        <div className="max-w-7xl mx-auto px-8 mt-4 ">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Previous campaign data restored
                </p>
                <p className="text-xs text-blue-600">
                  {campaignData?.campaignId 
                    ? `Campaign "${campaignData.campaignName}" (ID: ${campaignData.campaignId}) has been restored. Continue from where you left off.`
                    : 'Your form data has been automatically restored from the last session.'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCacheNotification(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-4">
        <p className="text-gray-600 mb-6">
          Configure your campaign, allocate budget, and submit creatives.
        </p>

        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          completedSteps={completedSteps}
          steps={STEPS}
        />

        {/* Step Content */}
        <div className="mt-8">
          {currentStep === 1 && (
            <CampaignDetailsForm
              ref={step1FormRef}
              campaignData={campaignData}
              setCampaignData={setCampaignData}
              onSaveDraft={handleSaveDraft}
              selectedRegion={selectedRegion}
              selectedBrand={selectedBrand}
            />
          )}

          {currentStep === 2 && (
            <POCSelectionForm
              ref={step2FormRef}
              campaignId={campaignData.campaignId}
              selectedPocs={selectedPOCs}
              onPocsChange={setSelectedPOCs}
              totalBudget={parseFloat(campaignData.totalBudget) || 0}
              region={selectedRegion}
            />
          )}

          {currentStep === 3 && (
            <CreativeUploadForm
              ref={step3FormRef}
              creativeData={creativeData}
              onCreativeDataChange={setCreativeData}
              campaignName={campaignData.campaignName}
            />
          )}
        </div>

        {/* Standardized Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between pt-6 border-t">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="ghost"
                onClick={currentStep === 2 ? handleStep2Back : handleStep3Back}
                disabled={loading}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            {currentStep === 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading}
              >
                Save Draft
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={currentStep === 1 ? handleStep1Next : handleStep2Next}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  'Next Step →'
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleStep3Submit}
                disabled={loading}
                className="bg-green-500 hover:bg-green-600 text-white px-8"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Campaign'
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCampaignCreation;